<script setup lang="ts">
/**
 * Aura On Board — New Take Over Client (CAU006) — Studio panel.
 * 자체완결 cdd(useCddFill) + normalizeOnBoardForWord. 서명은 [[Signers]]/이름 기반 auto-signer.
 * 문서: DR(Director/Secretary/RO) + Form 45(이사별) + Form 45B(비서별) + Service Agreement bundle.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, scopeOf, typeOf } from '../cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from '../cdd/agmSignersContext';
import OnBoardPackageDoc from '../OnBoardPackageDoc.vue';
import { normalizeOnBoardForWord, withCompanySignerFallback } from '../utils/normalizeOnBoardForWord';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const docRef = ref<InstanceType<typeof OnBoardPackageDoc> | null>(null);
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const autoLocal = ref<{ f45Ids: number[]; f45bIds: number[] }>({ f45Ids: [], f45bIds: [] });
/** Edit 재진입 — 저장값(restoreState)을 복원했으면 엔티티 자동채움이 덮어쓰지 않는다(빈 칸만 채움). */
const restoredFromSaved = ref(false);
/** restoreState 시점에 문서가 아직 안 붙었으면 Form 45/45B 사본 목록을 보관했다가 붙을 때 적용. */
let pendingDocLocal: any = null;
const taskConfigSteps = ref<string[]>([]);

const TASK_CODE = 'CAU006';
const DOC_TYPE = 'SG_ONBOARD_PRE';

let signerSeq = 0;
function mkSigner(name: string, title: string, role = 'director'): SignerEntry {
  return { id: `onb-sg-${signerSeq++}`, name: String(name || '').trim(), title, role, isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

/** 주소 파트 → 한 줄. 해외주소 우선, 없으면 SG 파트(block/street/building/#level-unit/postal). */
function buildAddr(dt: any): string {
  if (!dt || typeof dt !== 'object') return String(dt || '').trim();
  const g = (...keys: string[]) => { for (const k of keys) { const v = String((dt as any)[k] ?? '').trim(); if (v) return v; } return ''; };
  const direct = g('residentialAddress', 'primaryAddress', 'address');
  if (direct) return direct;
  const fl1 = g('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1', 'foreignLine1');
  const fl2 = g('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2', 'foreignLine2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const block = g('blockNo', 'BLOCK_NO', 'block');
  const street = g('streetName', 'STREET_NAME', 'street');
  const building = g('buildingName', 'BUILDING_NAME', 'building');
  const level = g('addrLevel', 'ADDR_LEVEL', 'level');
  const unitNo = g('addrUnit', 'ADDR_UNIT', 'unit');
  const postal = g('postalCode', 'POSTAL_CODE');
  const unit = [level, unitNo].filter(Boolean).join('-');
  return [[block, street].filter(Boolean).join(' '), building, unit ? `#${unit}` : '', postal ? `Singapore ${postal}` : '']
    .filter(Boolean).join(', ');
}

/** 국가 calling code — COUNTRY_CD(공통코드 B05xxx)는 코드값이라 basecode(CM)의 **BIGO3**(예 +65)로 조회한다. */
const _dialCache = new Map<string, string>();
async function dialCodeOf(countryCd: string): Promise<string> {
  const cc = String(countryCd || '').trim();
  if (!cc) return '';
  if (_dialCache.has(cc)) return _dialCache.get(cc)!;
  let bigo3 = '';
  try {
    const res: any = await baseCodeApi.getCodeByTotalCode('CM', cc);
    const code: any = res?.code || res?.data?.code || res?.data || {};
    bigo3 = String(code.bigo3 || code.BIGO3 || '').trim();
  } catch (e) { console.warn('[OnBoard] dialCodeOf failed', cc, e); }
  _dialCache.set(cc, bigo3);
  return bigo3;
}

/** contactCd → 인적 상세 보강(주소/이메일/전화/국적/ID). getContactDetail(entityCd, contactCd). */
async function enrichContact(cd: string, contactCd: string, positionCd?: string):
  Promise<{ id: string; address: string; email: string; mobile: string; nationality: string } | null> {
  const ccd = String(contactCd || '').trim();
  if (!cd || !ccd) return null;
  try {
    const res: any = await docGenApi.getContactDetail(cd, ccd, { addressScope: 'person', positionCd });
    const p: any = res?.data?.data ?? res?.data ?? res ?? {};
    // 전화 = BIGO3(국가 calling code, 예 +65) + 지역코드(areaCode) + 번호(mobileNo).
    //   COUNTRY_CD(예 B05200)는 공통코드값이라 basecode 의 BIGO3 로 조회해 붙인다.
    const dial = await dialCodeOf(String(p.countryCd ?? ''));
    const mobile = [dial, String(p.areaCode ?? '').trim(), String(p.mobileNo ?? '').trim()]
      .filter(Boolean).join(' ').trim();
    return {
      id: String(p.identificationNo || p.nricNo || p.contactRegNo || '').trim(),
      address: buildAddr(p),
      email: String(p.contactEmail || p.email || '').trim(),
      mobile,
      nationality: String(p.nationalityName || p.nationality || '').trim(),
    };
  } catch (e) { console.warn('[OnBoard] enrichContact failed', ccd, e); return null; }
}

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const pct = Math.min(100, Math.round((fill.filledCount.value / total) * 100));
  emit('progress-change', pct);
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

/** person 필드 → ✨ 기본 조회 역할을 필드 맥락으로 결정. */
function lookupRoleFor(key: string): { role: string; scoped: boolean } {
  const b = bareKey(key);
  const sc = scopeOf(key);
  // Form 45/45B = **신규 선임** 대상 → 특정 엔티티 임원이 아니라 client-wide 전체 CONTACT 에서 고른다.
  if (/^f45_/.test(sc) || /^f45b_/.test(sc)) return { role: 'CONTACT', scoped: false };
  if (b === 'secretary_name') return { role: 'SECRETARY', scoped: true };
  if (b === 'director_name') return { role: 'DIRECTOR', scoped: true };
  // DR 사임(outgoing) 행 = 현재 이 회사 임원 중에서 고른다. (선임 행 appt_* 은 새 사람 → CONTACT)
  if (/^resign_dir_\d+_director_name$/.test(b)) return { role: 'DIRECTOR', scoped: true };
  if (/^resign_sec_\d+_secretary_name$/.test(b)) return { role: 'SECRETARY', scoped: true };
  if (b === 'ubo_name' || b === 'shareholder_name' || b === 'member_name') return { role: 'SHAREHOLDER', scoped: true };
  return { role: 'CONTACT', scoped: false };
}
/** ✨ 조회를 요청한 칸 — 결과는 이 칸에 넣는다(조회 중 다른 칸을 클릭해도 그 칸에 들어가지 않게). */
let lookupKey = '';
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    lookupKey = key;
    const { role, scoped } = lookupRoleFor(key);
    emit('request-person-lookup', {
      fieldGroup: 'aura-onboard',
      key: bareKey(key),
      scopedKey: key,
      defaultRole: role,
      lookupType: 'officer',
      entityCd: scoped ? entityCd.value : '',
    });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

// ── 자동채움: 회사 선택 → 회사·이사·비서·주주 ──────
async function autofillFromEntity(entityCdRaw: string) {
  const cd = String(entityCdRaw || '').trim();
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  // ⚠ Edit 재진입: 자동채움(비동기)이 복원 뒤에 끝나면서 저장한 회사측 서명 이사를 Schedule 1 첫 이사로,
  //   Form 45 사본 목록을 빈 목록으로 덮었다. 다른 회사로 바꾼 경우에만 새로 채운다.
  if (entityChanged) restoredFromSaved.value = false;
  if (!restoredFromSaved.value) for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) {
    signersCtx.clearAll();
    for (const k of Object.keys(fill.values)) {
      if (/^(f45_\d+\/|f45b_\d+\/)/.test(k) || /^(appt_|resign_|bnd_)/.test(k)) fill.setValue(k, '');
    }
  }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = String(v ?? '').trim();
    if (!s) return;
    written.add(k);
    if (restoredFromSaved.value && String(fill.values[k] ?? '').trim()) return; // 저장값 보존
    fill.setValue(k, s);
  };

  let members: any[] = [];

  // 1) L_ENTITY 원본 — 회사명 / 등록번호 / 등록사무소 주소 / UEN / 주주
  //   ⚠ On Board 는 **새 이사/비서를 선임**하는 업무다. 선임 대상은 아직 엔티티 임원명단에 없는
  //     사람이므로 이사·비서(Stage 1·2)와 Form 45/45B 는 **자동바인딩하지 않는다**(운영자 수동 입력).
  //     회사 정보와 (인수인계라 이미 존재하는) 주주만 자동채운다.
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    set('company_uen', src.registrationNo);
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    members = Array.isArray(src.members) ? src.members : [];
  } catch (e) { console.warn('[OnBoard] agm-source failed', e); }

  // 2) Members(주주) → Service Agreement Schedule 1 **Shareholders** 만 자동채움(실존).
  //   ⚠ **Principal(UBO)** 은 Nominee Director 약정에서 명목이사에게 지시하는 실소유자로,
  //     엔티티에 일반적으로 존재하는 값이 아니다(Nominee Director 서비스 선택 시에만 해당).
  //     주주 첫 명 ≠ Principal 이므로 **자동채우지 않는다**(운영자 입력).
  if (members.length) {
    set('bnd_sh_count', String(members.length));
    // 주주 주소는 목록에 없음 → contactCd 로 상세 병렬 보강.
    const enrichedSh = await Promise.all(members.map((m) => enrichContact(cd, String(m?.contactCd || ''))));
    members.forEach((m, i) => {
      const n = i + 1;
      set(`bnd_sh_${n}_shareholder_name`, m?.name);
      set(`bnd_sh_${n}_shareholder_identification_no`, m?.registrationNo || m?.identificationNo || enrichedSh[i]?.id);
      set(`bnd_sh_${n}_shareholder_address`, m?.address || m?.residentialAddress || enrichedSh[i]?.address);
    });
  }

  // 3) 현 이사(officer) → (a) Service Agreement **Schedule 1 Directors**(현재 명단, 기록용) 자동채움
  //    + (b) DR 결의 서명자(현 이사회) 자동시드.
  //   ⚠ DR **선임 목록**(appt_dir)과 Form 45 는 여기서 채우지 않는다 — 그건 **새로 선임**되는 이사라 운영자 입력.
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'director' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    const officers = [...(off.autoOfficers || []), ...(off.manualOfficers || [])]
      .filter((o: any) => String(o?.status || '') !== 'Cessation');
    // 이름 + contactCd 수집(중복 제거) → contact 상세로 ID/주소/연락처/국적 병렬 보강.
    const rawDirs: Array<{ name: string; contactCd: string }> = [];
    for (const o of officers) {
      const nm = String(o.fullName || o.name || '').trim();
      if (nm && !rawDirs.some((d) => d.name === nm)) {
        rawDirs.push({ name: nm, contactCd: String(o.contactCode || o.contactCd || o.CONTACT_CD || '').trim() });
      }
    }
    const enrichedDirs = await Promise.all(rawDirs.map((d) => enrichContact(cd, d.contactCd)));
    const dirs = rawDirs.map((d, i) => ({
      name: d.name,
      id: enrichedDirs[i]?.id || '',
      address: enrichedDirs[i]?.address || '',
      mobile: enrichedDirs[i]?.mobile || '',
      email: enrichedDirs[i]?.email || '',
    }));
    if (dirs.length) {
      // (a) Schedule 1 Directors (현재 명단)
      set('bnd_dir_count', String(dirs.length));
      dirs.forEach((d, i) => {
        const n = i + 1;
        set(`bnd_dir_${n}_director_name`, d.name);
        set(`bnd_dir_${n}_director_identification_no`, d.id);
        set(`bnd_dir_${n}_director_address`, d.address);
        set(`bnd_dir_${n}_director_mobile_no`, d.mobile);
        set(`bnd_dir_${n}_director_email`, d.email);
      });
      // 서비스계약 실행 이사(회사측 서명자) 기본값 = 첫 현 이사
      set('director_name', dirs[0].name);
      set('director_identification_no', dirs[0].id);
      // (b) DR 결의 서명자(현 이사회) + Authorised Persons
      for (const sk of ['dr_director_Signers', 'dr_secretary_Signers', 'dr_ro_Signers', 'onboard_ap_Signers']) {
        if (!signersCtx.signersOf(sk).length) signersCtx.setSigners(sk, dirs.map((d) => mkSigner(d.name, 'Director')));
      }
    }
  } catch (e) { console.warn('[OnBoard] director autofill failed', e); }

  // Form 45 는 자동 시드하지 않는다(새 선임 이사라 운영자가 "+ Add" 로 추가).
  // Form 45B 는 VL 고정본(비서 = FLORENSIA LEOVANNY LIONG)이라 고를 사람이 없어 **1부 자동** — 선임 없는 작업이면 ✕ 로 삭제.
  //   복원된 태스크면 사본 목록을 건드리지 않는다(비우면 Update 때 Form 45 문서가 삭제됨 / 지운 45B 가 되살아남).
  if (!restoredFromSaved.value) autoLocal.value = { f45Ids: [], f45bIds: [1] };
  autofilledKeys.value = written;
  emitProgress();
}

watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

// 선택 인물 → 활성 키
//   ⚠ 예전엔 "선택 순간 포커스가 있는 칸(activeKey)"에 넣어, ✨(Name) 후 Designation 칸을 클릭한 채
//     사람을 고르면 이름이 Designation 에 들어가고 Name 은 빈 채로 문서가 나갔다.
watch(() => props.selectedPerson, async (p) => {
  const key = lookupKey || fill.activeKey.value;
  if (!p || !key) return;
  lookupKey = '';
  fill.applyPersonToKey(key, p as Record<string, any>);
  // 전화 국가코드(BIGO3, 예 +65) 보강 — applyPersonToKey 는 sync 라 여기서 갱신.
  const cc = String((p as any).countryCd || '').trim();
  const mobileNo = String((p as any).mobileNo || '').trim();
  if (cc && mobileNo) {
    const dial = await dialCodeOf(cc);
    const stem = bareKey(key).replace(/_name$|_holder$/, '');
    const mobile = [dial, String((p as any).areaCode || '').trim(), mobileNo].filter(Boolean).join(' ').trim();
    if (mobile) fill.setValue(scopeOf(key) + `${stem}_mobile_no`, mobile);
  }
  emitProgress();
});
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value);
  emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  const line = sgAppliedAddressLine(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

// ── Form 45/45B → DR 선임 목록 자동 연동 (Form 45 가 소스, 일방향) ──
//   Form 45 = "그 이사를 선임"하는 consent 문서. 거기에 이름/ID 를 채우면
//   DR Change of Director 의 APPOINTMENT 목록(appt_dir)에 자동 반영. 비서(Form 45B → appt_sec) 동일.
let syncingAppt = false;
function syncAppointmentsFromForms() {
  if (syncingAppt) return;
  syncingAppt = true;
  try {
    const local: any = docRef.value?.gatherLocalState?.() || {};
    const f45Ids: number[] = Array.isArray(local.f45Ids) ? local.f45Ids : [];
    const f45bIds: number[] = Array.isArray(local.f45bIds) ? local.f45bIds : [];
    // Directors ← Form 45
    let dcnt = 0;
    for (const id of f45Ids) {
      const nm = fill.getValue(`f45_${id}/director_name`);
      const idno = fill.getValue(`f45_${id}/director_identification_no`);
      if (String(nm || '').trim() || String(idno || '').trim()) {
        dcnt += 1;
        fill.setValue(`appt_dir_${dcnt}_director_name`, nm);
        fill.setValue(`appt_dir_${dcnt}_director_identification_no`, idno);
        // 발효일 연동: DR 선임행 ↔ Form 45 (채워진 쪽이 소스).
        const edDr = String(fill.getValue(`appt_dir_${dcnt}_effective_date`) || '').trim();
        const edF45 = String(fill.getValue(`f45_${id}/effective_date`) || '').trim();
        if (edDr) fill.setValue(`f45_${id}/effective_date`, edDr);
        else if (edF45) fill.setValue(`appt_dir_${dcnt}_effective_date`, edF45);
      }
    }
    for (let i = dcnt + 1; i <= dcnt + 12; i++) {
      fill.setValue(`appt_dir_${i}_director_name`, '');
      fill.setValue(`appt_dir_${i}_director_identification_no`, '');
    }
    fill.setValue('appt_dir_count', String(dcnt));
    // Secretaries ← Form 45B
    let scnt = 0;
    for (const id of f45bIds) {
      const nm = fill.getValue(`f45b_${id}/secretary_name`);
      const idno = fill.getValue(`f45b_${id}/secretary_identification_no`);
      if (String(nm || '').trim() || String(idno || '').trim()) {
        scnt += 1;
        fill.setValue(`appt_sec_${scnt}_secretary_name`, nm);
        fill.setValue(`appt_sec_${scnt}_secretary_identification_no`, idno);
      }
    }
    for (let i = scnt + 1; i <= scnt + 12; i++) {
      fill.setValue(`appt_sec_${i}_secretary_name`, '');
      fill.setValue(`appt_sec_${i}_secretary_identification_no`, '');
    }
    fill.setValue('appt_sec_count', String(scnt));
  } finally { syncingAppt = false; }
}
// ── Schedule 1 Principal(s) → Nominee Director 계약 당사자 "Principal" 칸 (비었거나 자동값일 때만) ──
//   고객은 Schedule 1 표에 Principal 을 넣는데, 계약 당사자 줄([[ubo_name]] (Passport No. …) of …)은
//   별도 칸이라 비어서 나갔다. 운영자가 직접 고친 값은 덮지 않는다.
const lastAutoPrincipal: Record<string, string> = {};
function followPrincipalFromSchedule() {
  const n = parseInt(String(fill.values.bnd_ubo_count || '0'), 10) || 0;
  const rows = Array.from({ length: n }, (_, i) => i + 1).map((i) => ({
    name: String(fill.values[`bnd_ubo_${i}_ubo_name`] || '').trim(),
    id: String(fill.values[`bnd_ubo_${i}_ubo_identification_no`] || '').trim(),
    addr: String(fill.values[`bnd_ubo_${i}_ubo_residential_address`] || '').trim(),
  })).filter((r) => r.name);
  const derived: Record<string, string> = {
    ubo_name: rows.map((r) => r.name).join(' and '),
    ubo_identification_no: rows.map((r) => r.id).filter(Boolean).join(' / '),
    ubo_residential_address: [...new Set(rows.map((r) => r.addr).filter(Boolean))].join('; '),
  };
  for (const [k, v] of Object.entries(derived)) {
    const cur = String(fill.values[k] || '');
    if (cur === v) { lastAutoPrincipal[k] = v; continue; } // 같은 값(Edit 복원 포함) = 자동값으로 본다
    if (cur.trim() && cur !== lastAutoPrincipal[k]) continue; // 운영자 입력 유지
    lastAutoPrincipal[k] = v;
    fill.setValue(k, v);
  }
}
watch(() => fill.values, () => { syncAppointmentsFromForms(); followPrincipalFromSchedule(); }, { deep: true });

/** 고객 수정본(2026-10) 기본값 — 체크(표준 비서 서비스, 등록부 위치 = 회사)·Aura 서명자·Compilation 요금. 새 task 에서 빈 값만. */
const BUNDLE_DEFAULTS: Record<string, string> = {
  service_secretarial: 'yes', roc_location: 'company', rond_location: 'company', rons_location: 'company',
  aura_signatory_name: 'FLORENSIA LEOVANNY LIONG', aura_signatory_designation: 'Managing Director',
  fee_compilation_active: '1,800',
};
onMounted(async () => {
  if (!editMode.value) {
    for (const [k, v] of Object.entries(BUNDLE_DEFAULTS)) if (!String(fill.values[k] ?? '').trim()) fill.setValue(k, v);
  }
  emitProgress();
  syncAppointmentsFromForms();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) {
    if (docRef.value?.restoreLocalState) { docRef.value.restoreLocalState(s.local); syncAppointmentsFromForms(); }
    else pendingDocLocal = s.local;
  }
}
watch(docRef, (d) => {
  if (d && pendingDocLocal) { d.restoreLocalState?.(pendingDocLocal); pendingDocLocal = null; syncAppointmentsFromForms(); }
});

function autoSigners(names: Array<{ name: unknown; title: string }>): any[] | undefined {
  const out: any[] = [];
  names.forEach((n, i) => {
    const nm = String(n.name ?? '').trim();
    if (!nm) return;
    out.push({ id: `onb-${i}`, name: nm, title: n.title, role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' });
  });
  return out.length ? out : undefined;
}

type OnBoardKind = 'dr_director' | 'dr_secretary' | 'dr_ro' | 'form45' | 'form45b' | 'bundle' | 'other';
function classifyDoc(name: string): OnBoardKind {
  const n = String(name || '').toLowerCase().replace(/[_\-.]+/g, ' ');
  if (/form\s*45b/.test(n)) return 'form45b';
  if (/form\s*45/.test(n)) return 'form45';
  if (/change of director|change in director/.test(n)) return 'dr_director';
  if (/change of secretary/.test(n)) return 'dr_secretary';
  if (/change in ro|registered office|change in registered/.test(n)) return 'dr_ro';
  if (/on board|new client|take over|service agreement/.test(n)) return 'bundle';
  return 'other';
}

const F45_OWNED = [
  'director_name', 'director_residential_address', 'director_contact_address', 'director_identification_no',
  'director_nationality', 'director_email', 'director_mobile_no', 'dated_on', 'effective_date',
  'witness_name', 'witness_identification_no', 'witness_occupation', 'witness_date', 'witness_signature',
];
const F45B_OWNED = [
  'secretary_name', 'secretary_residential_address', 'secretary_contact_address',
  'secretary_identification_no', 'secretary_nationality', 'effective_date', 'dated_on',
];

function scopedFormData(scopePrefix: string, owned: string[]): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) {
    if (k.includes('/')) continue;
    if (owned.includes(bareKey(k))) continue;
    out[k] = v;
  }
  for (const [k, v] of Object.entries(base)) {
    if (!k.startsWith(scopePrefix)) continue;
    const bk = bareKey(k);
    const sv = String(v ?? '').trim();
    if (sv === '' && bk in out && String(out[bk] ?? '').trim() !== '') continue;
    out[bk] = v;
  }
  return normalizeOnBoardForWord(out);
}
function companyFormData(): Record<string, any> {
  const base = fill.exportPayload();
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(base)) { if (!k.includes('/')) out[k] = v; }
  return withCompanySignerFallback(normalizeOnBoardForWord(out));
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    syncAppointmentsFromForms(); // Form 45/45B 사본 목록 기준으로 DR 선임 행 최신화(재진입 후 값 변경 없이 Update 하는 경우)
    const companyBase = companyFormData();
    const local: any = docRef.value?.gatherLocalState?.() || { f45Ids: [], f45bIds: [] };
    const companyLabel = String(companyBase.company_name || 'Company');

    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: DOC_TYPE,
      isMyPreset: props.selection?.isMyPreset === true,
      formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'On Board — New Take Over Client',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return {
              createDocument: steps.length === 0 ? true : steps.includes('doc'),
              requestTSign: steps.includes('t-sign'),
              proceedWithEFiling: steps.includes('e-filing'),
            };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    progress.value = 'Preparing documents…';
    const rawDocs = props.selection.documents || [];
    const seenTmpl = new Set<number>();
    const docs = rawDocs.filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true;
      if (seenTmpl.has(tid)) return false;
      seenTmpl.add(tid);
      return true;
    });
    const stripSuffix = (s: string) => s.split(' — ')[0].trim();
    type DocSave = { templateId: number; label: string; personName: string; formData: Record<string, any>; signers?: any[] };
    const docsToSave: DocSave[] = [];
    function pushOnce(d: any, formData: Record<string, any>, person: string, suffix = '', signers?: any[]) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = stripSuffix(String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, ''));
      docsToSave.push({
        templateId: tid,
        label: suffix ? `${baseLabel} — ${suffix}` : baseLabel,
        personName: person,
        formData: normalizeOnBoardForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }
    const pick = (key: string, fallback?: any[]) => {
      const s = signersCtx.signersOf(key);
      return s.length ? s : (fallback && fallback.length ? fallback : undefined);
    };

    for (const d of docs) {
      const kind = classifyDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'form45') {
        // Form 45 = 서명선 + 이름/직책(수기서명) — T-Sign [[Signers]] 없음 → signers 미전달.
        const ids: number[] = local.f45Ids?.length ? local.f45Ids : [];
        for (const id of ids) {
          const fd = scopedFormData(`f45_${id}/`, F45_OWNED);
          const person = String(fd.director_name || `Director ${id}`);
          pushOnce(d, fd, person, ids.length > 1 ? `Director ${id}` : '');
        }
        continue;
      }
      if (kind === 'form45b') {
        const ids: number[] = local.f45bIds?.length ? local.f45bIds : [];
        for (const id of ids) {
          const fd = scopedFormData(`f45b_${id}/`, F45B_OWNED);
          const person = String(fd.secretary_name || `Secretary ${id}`);
          pushOnce(d, fd, person, ids.length > 1 ? `Secretary ${id}` : '');
        }
        continue;
      }
      // 회사 단위 문서 (DR 3종 + bundle)
      // 포함 여부(YES/NO) — DR 3종은 운영자가 문서 상단 토글로 제외 가능('no' 면 skip).
      const INCLUDE_FLAG: Record<string, string> = {
        dr_director: 'include_dr_director', dr_secretary: 'include_dr_secretary', dr_ro: 'include_dr_ro',
      };
      const incFlag = INCLUDE_FLAG[kind];
      if (incFlag && String(fill.values[incFlag] ?? '').toLowerCase() === 'no') continue;
      const signerKey =
        kind === 'dr_director' ? 'dr_director_Signers'
          : kind === 'dr_secretary' ? 'dr_secretary_Signers'
            : kind === 'dr_ro' ? 'dr_ro_Signers'
              : 'onboard_ap_Signers';
      const autoName = String(companyBase.director_name || fill.values.director_name || '').trim();
      const signers = pick(signerKey, autoName ? autoSigners([{ name: autoName, title: 'Director' }]) : undefined);
      pushOnce(d, companyBase, companyLabel, '', signers);
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) { progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`; }
      } catch (err: any) {
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
void scopeOf;
</script>

<template>
  <div class="agm-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'On Board — New Take Over Client' }}</div>
        <div class="co-subtitle">Aura On Board — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <OnBoardPackageDoc ref="docRef" :auto-local="autoLocal" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.agm-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.agm-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
